import type { Theme } from '@material-ui/core/styles'
import { makeStyles } from '@material-ui/core/styles'
import ErrorIcon from '@material-ui/icons/Error'
import { QuoteContext } from '@paintscout/react-quote'
import {
  Grid,
  AdornmentBox,
  InputField,
  KeypadInputField,
  OverridableInputField,
  useClientOptions,
  DropdownSelect,
  useDialogs,
  ConfirmationDialog
} from '@paintscout/ui'
import type { QuoteDetails } from '@paintscout/util/builder'
import { getObjectLabels, getDetails, setDetails } from '@paintscout/util/builder'
import type { OverridableValue, QuoteDocument } from 'paintscout'
import React, { useContext, useEffect, useState } from 'react'
import DetailsItem from './DetailsItem'

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  alignRightInput: {
    textAlign: 'right'
  },
  adornmentSelect: {
    paddingRight: theme.spacing(1)
  },
  inputClickHolder: {
    display: 'inline-block',
    position: 'relative'
  },
  inputClickChild: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0
  }
}))

interface SubTotalItemProps {
  onChange: (args: { quote: QuoteDocument }) => void
  disabled: boolean
  expanded: boolean
  onExpand: (expanded: boolean) => void
}

export function SubtotalItem(props: SubTotalItemProps) {
  const { onChange, disabled, expanded, onExpand } = props
  const { openDialog, dismissDialog } = useDialogs()
  const { quote } = useContext(QuoteContext)
  const { options } = useClientOptions()
  const { currency, quote: quoteLables } = getObjectLabels({ options })
  const [details, setStateDetails] = useState(getDetails({ quote, options }))
  const [subtotalValue, setSubtotalValue] = useState({
    default: details.serviceFees?.hiddenValue ? details.totals.afterAdjustment : details.totals.price,
    custom: details.totals.afterAdjustment,
    useCustom: !!details.totalAdjustment.rate
  })
  const classes = useStyles({})
  const hasHiddenServiceFee = !!(Number(details.serviceFees?.hiddenValue) ?? 0)
  const quoteTypeSubTotals = quote?.int_ext?.value === 'all' && options?.options?.quotes?.groupHours ? true : false
  const warningMessage = `Adjusting the subtotal will hide the breakdown of ${quoteLables.value} type totals. Would you like to continue? You can revert to the original subtotal if needed.`
  const subTotal =
    details.totals.price === details.totals.afterAdjustment ? details.totals.price : details.totals.afterAdjustment

  useEffect(() => {
    const updatedDetails = getDetails({ quote, options })
    setStateDetails(updatedDetails)
    setSubtotalValue({
      default: updatedDetails.totals.price,
      custom: updatedDetails.totals.afterAdjustment,
      useCustom: !!updatedDetails.totalAdjustment.rate
    })
  }, [quote])

  const updateDetails = (updatedDetails: QuoteDetails) => {
    setStateDetails(updatedDetails)
    onChange({ quote: setDetails({ quote, options, details: updatedDetails }) })
  }
  const showOverridden = details.totalAdjustment?.rate || details?.serviceFees?.hiddenValue > 0

  const adjustmentTypeOptions = [
    { label: '%', value: 'percentage' },
    { label: currency.symbol, value: 'value' }
  ]

  return (
    <DetailsItem
      label={'Sub Total'}
      value={subTotal}
      expandIcon={showOverridden ? <ErrorIcon /> : null}
      expanded={expanded}
      onExpand={onExpand}
      detailsComponent={
        <Grid container direction="row" justifyContent="flex-end" spacing={2}>
          <Grid className={disabled ? classes.inputClickHolder : null} item={true} xs={12}>
            <KeypadInputField
              value={details.totalAdjustment?.rate !== undefined ? details.totalAdjustment.rate : ''}
              disabled={disabled}
              fullWidth={true}
              margin="dense"
              format={'price'}
              label={`Manual Adjustment`}
              noAdornmentMargin
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (event.target.value && !details.totalAdjustment.rate && quoteTypeSubTotals) {
                  openDialog(ConfirmationDialog, {
                    title: 'Warning',
                    message: warningMessage,
                    yesLabel: 'Confirm',
                    noLabel: 'Cancel',
                    scary: true,
                    onConfirm: () => {
                      dismissDialog()
                      updateAdjustment('rate', event.target.value)
                    },
                    onCancel: () => {
                      dismissDialog()
                    }
                  })
                } else {
                  updateAdjustment('rate', event.target.value)
                }
              }}
              classes={{ inputInput: classes.alignRightInput }}
              style={{ width: '100%' }}
              endAdornment={
                <AdornmentBox>
                  <DropdownSelect
                    variant="single"
                    disabled={disabled}
                    nestedDropdown
                    options={adjustmentTypeOptions}
                    onChange={(option) => {
                      updateAdjustment('type', option.value)
                    }}
                    value={
                      adjustmentTypeOptions.find((option) => option.value === details.totalAdjustment.type) ||
                      adjustmentTypeOptions[0]
                    }
                  />
                </AdornmentBox>
              }
              autoSelect={true}
            />
            <div className={disabled ? classes.inputClickChild : null}></div>
          </Grid>
          {hasHiddenServiceFee && (
            <Grid className={disabled ? classes.inputClickHolder : null} item xs={12}>
              <KeypadInputField
                value={quote.totals?.service_fee_hidden?.toFixed(2) ?? '0.00'}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  updateHiddenServiceFee(event.target.value)
                }}
                disabled={true}
                // disabled={!companyOptions.serviceFees.allowCustom}
                fullWidth={true}
                margin="dense"
                format={'price'}
                label={`Service Fee (${
                  details.serviceFees.hiddenValue !== undefined ? details.serviceFees.hiddenValue : ''
                }%)`}
                classes={{ inputInput: classes.alignRightInput }}
                style={{ width: '100%' }}
              />
              <div className={disabled ? classes.inputClickChild : null}></div>
            </Grid>
          )}

          <Grid className={disabled ? classes.inputClickHolder : null} item={true} xs={12}>
            <>
              {!hasHiddenServiceFee && (
                <OverridableInputField
                  label={`Sub Total (${currency.symbol})`}
                  fullWidth={true}
                  value={subtotalValue}
                  disabled={disabled}
                  resetBlank={true}
                  classes={{ inputInput: classes.alignRightInput }}
                  onChange={handleSubtotalChange}
                />
              )}
              {hasHiddenServiceFee && (
                <InputField
                  disabled
                  fullWidth={true}
                  value={details.totals.afterAdjustment}
                  label={`Sub Total (${currency.symbol})`}
                />
              )}
              <div className={disabled ? classes.inputClickChild : null}></div>
            </>
          </Grid>
          <Grid className={disabled ? classes.inputClickHolder : null} item xs={12}>
            <InputField
              value={details.totalAdjustment.description}
              disabled={disabled}
              label={'Description / Reason'}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                updateAdjustment('description', event.target.value)
              }}
              fullWidth={true}
              margin="dense"
              multiline={true}
            />
            <div className={disabled ? classes.inputClickChild : null}></div>
          </Grid>
        </Grid>
      }
    />
  )

  function handleSubtotalChange(updatedValue: OverridableValue) {
    let rate = 0
    let customValue
    if (updatedValue.useCustom) {
      customValue = typeof updatedValue.custom === 'string' ? parseFloat(updatedValue.custom) : updatedValue.custom
      rate = customValue - details.totals.price
      if (!details.totalAdjustment.rate) {
        openDialog(ConfirmationDialog, {
          title: 'Warning',
          message: warningMessage,
          yesLabel: 'Confirm',
          noLabel: 'Cancel',
          scary: true,
          onConfirm: () => {
            dismissDialog()

            updateDetails({
              ...details,
              totalAdjustment: {
                ...details.totalAdjustment,
                type: 'value',
                rate
              },
              totals: {
                ...details.totals,
                afterAdjustment: updatedValue.useCustom ? customValue : details.totals.afterAdjustment
              }
            })
          },
          onCancel: () => {
            dismissDialog()
            updateDetails({
              ...details,
              totalAdjustment: {
                ...details.totalAdjustment,
                type: 'value'
              },
              totals: {
                ...details.totals
              }
            })
          }
        })
      }
    }
    updateDetails({
      ...details,
      totalAdjustment: {
        ...details.totalAdjustment,
        type: 'value',
        rate
      },
      totals: {
        ...details.totals,
        afterAdjustment: updatedValue.useCustom ? customValue : details.totals.afterAdjustment
      }
    })
  }

  function updateHiddenServiceFee(value: string) {
    updateDetails({
      ...details,
      serviceFees: {
        ...details?.serviceFees,
        hiddenValue: Number(value)
      }
    })
  }

  function updateAdjustment(key: keyof QuoteDetails['totalAdjustment'], value: string) {
    updateDetails({
      ...details,
      totalAdjustment: {
        ...details.totalAdjustment,
        [key]: value
      }
    })
  }
}
